import { useEffect, useState } from "react";
import axios from "axios";
import { Reveal } from "./Reveal";
import { Instagram, ArrowUpRight, PlayCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IG_HANDLE = "dr.yasminimdad";

export default function Reels() {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/reels`).then((r) => {
      setReels(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <section id="reels" className="py-24 md:py-32 relative overflow-hidden">
      <div
        className="absolute -top-32 -left-40 w-[36rem] h-[36rem] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle,#E4C3C9 0%,transparent 70%)" }}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <Reveal>
            <span className="overline text-accent">Latest from Instagram</span>
            <h2 className="font-serif text-primary text-4xl sm:text-5xl tracking-tight leading-[1.02] mt-5 max-w-2xl">
              Notes from the clinic, in short.
            </h2>
            <p className="mt-6 text-muted-foreground max-w-lg text-sm sm:text-base leading-relaxed">
              Bite-sized reels from Dr. Yasmin — the questions she is asked most, myth-busted plainly, and the answers she wishes every woman heard sooner.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={`https://www.instagram.com/${IG_HANDLE}/`}
              target="_blank"
              rel="noreferrer"
              data-testid="ig-follow-link"
              className="group relative inline-flex items-center gap-3 rounded-full pl-2 pr-6 py-2 font-semibold text-white overflow-hidden hover:-translate-y-0.5 transition-transform duration-300"
              style={{
                background:
                  "linear-gradient(135deg,#F58529 0%,#DD2A7B 40%,#8134AF 75%,#515BD4 100%)",
                boxShadow: "0 18px 40px -14px rgba(221,42,123,0.55)",
              }}
            >
              <span className="w-11 h-11 rounded-full bg-white/15 grid place-items-center backdrop-blur-sm">
                <Instagram size={18} />
              </span>
              <span className="tracking-tight">Follow @{IG_HANDLE}</span>
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-2xl border border-border bg-secondary/20 animate-pulse" style={{ aspectRatio: "4/5" }} />
            ))}
          </div>
        ) : reels.length === 0 ? (
          <Reveal>
            <div className="border border-dashed border-border rounded-[1.5rem] p-14 text-center bg-secondary/10" data-testid="reels-empty">
              <PlayCircle className="mx-auto text-accent mb-4" size={24} />
              <p className="font-serif text-2xl text-primary">Reels coming soon.</p>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                We&rsquo;re preparing a series of short, evidence-led videos on pregnancy and women&rsquo;s health. Follow us on Instagram to catch each one.
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reels.map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 0.08}>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`reel-card-${i}`}
                  className="group block rounded-2xl border border-border overflow-hidden bg-card hover:-translate-y-1 hover:border-accent transition-[transform,border-color] duration-300"
                >
                  <div className="relative bg-secondary/20 overflow-hidden" style={{ aspectRatio: "4/5" }}>
                    <iframe
                      title={`Instagram reel ${r.shortcode}`}
                      src={`https://www.instagram.com/reel/${r.shortcode}/embed/`}
                      className="w-full h-full"
                      allow="encrypted-media"
                      loading="lazy"
                      scrolling="no"
                      frameBorder="0"
                    />
                  </div>
                  {r.caption ? (
                    <div className="px-5 py-4 border-t border-border">
                      <p className="text-sm text-foreground/80 line-clamp-2">{r.caption}</p>
                    </div>
                  ) : null}
                </a>
              </Reveal>
            ))}
          </div>
        )}

        {reels.length > 0 && (
          <Reveal delay={0.15}>
            <div className="mt-14 rounded-[1.5rem] p-8 sm:p-10 relative overflow-hidden text-white text-center"
              style={{
                background:
                  "linear-gradient(135deg,#F58529 0%,#DD2A7B 45%,#8134AF 80%,#515BD4 100%)",
              }}
            >
              <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.6) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.4) 0%, transparent 40%)" }} />
              <div className="relative">
                <Instagram className="mx-auto mb-4" size={26} />
                <p className="font-serif text-3xl sm:text-4xl leading-tight max-w-xl mx-auto">
                  Get every new reel from Dr. Yasmin.
                </p>
                <p className="mt-3 text-sm sm:text-base text-white/85 max-w-md mx-auto">
                  Evidence-based bites on pregnancy, fertility and women&rsquo;s health — no fluff, no fear-mongering.
                </p>
                <a
                  href={`https://www.instagram.com/${IG_HANDLE}/`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="ig-follow-large-btn"
                  className="group mt-7 inline-flex items-center gap-3 rounded-full bg-white text-[#8134AF] pl-2 pr-7 py-2 font-semibold hover:-translate-y-0.5 transition-transform duration-300"
                  style={{ boxShadow: "0 20px 40px -14px rgba(0,0,0,0.35)" }}
                >
                  <span className="w-11 h-11 rounded-full grid place-items-center text-white" style={{ background: "linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)" }}>
                    <Instagram size={18} />
                  </span>
                  <span className="tracking-tight">Follow @{IG_HANDLE}</span>
                  <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
